// Modes de jeu (dossier §7). Toutes les valeurs sont des propositions à mesurer.

export type ModeKind = 'ffa' | 'tdm' | 'dom' | 'survival' | 'private';
export type TeamFormat = '2v2' | '4v4' | '8v8';
export type DifficultyId = 'facile' | 'normal' | 'moyen' | 'difficile' | 'ultra' | 'tankshift';

export const TEAM_FORMATS: Record<TeamFormat, { size: number; kills: number; half: number }> = {
  '2v2': { size: 2, kills: 30, half: 1800 },
  '4v4': { size: 4, kills: 60, half: 2300 },
  '8v8': { size: 8, kills: 100, half: 2900 },
};

export const TDM = { durationS: 600, overtimeMaxS: 180 };
export const DOM = { durationS: 720, target: 1000, captureS: 8, pointRadius: 230, suddenDeathS: 120 };
/** En équipes : part de l'XP cumulée conservée à la mort. */
export const TEAM_XP_KEEP = 0.75;
export const TEAM_RESPAWN_S = 3;
/** Récompenses en équipes : participation complète et victoire (proratisées si match interrompu). */
export const TEAM_REWARD = { participation: 100, victory: 50 };
/** Délai d'attente avant de compléter une équipe avec des bots annoncés. */
export const MATCH_FILL_S = 20;
export const MATCH_RESULTS_S = 12;

export const SURVIVAL = { maxWaves: 30, prepS: 20, bossEvery: 5, half: 1800, maxPlayers: 6, coinsPerWave: 20, minShare: 0.05 };

export const DIFFICULTIES: Record<DifficultyId, { hp: number; dmg: number; reward: number }> = {
  facile: { hp: 0.5, dmg: 0.5, reward: 0.5 },
  normal: { hp: 1, dmg: 1, reward: 1 },
  moyen: { hp: 2, dmg: 2, reward: 1.5 },
  difficile: { hp: 3, dmg: 3, reward: 2 },
  ultra: { hp: 5, dmg: 5, reward: 2.5 },
  tankshift: { hp: 20, dmg: 20, reward: 5 },
};

/** Facteur PV selon le nombre de joueurs ; les dégâts ne sont pas remultipliés par le nombre de joueurs. */
export function survivalHpFactor(players: number): number {
  return 1 + 0.6 * (Math.max(1, players) - 1);
}

export function survivalEnemyCount(base: number, players: number): number {
  return Math.round(base * (1 + 0.25 * (Math.max(1, players) - 1)));
}

/** Composition d'une vague : nombre de base croissant, palier de monstres de 1 à 3. */
export function waveBase(wave: number): { count: number; tier: 1 | 2 | 3; boss: boolean } {
  return { count: 3 + Math.floor(wave * 1.5), tier: (wave <= 10 ? 1 : wave <= 20 ? 2 : 3) as 1 | 2 | 3, boss: wave % SURVIVAL.bossEvery === 0 };
}

export interface PrivateOptions {
  map: 'A' | 'B' | 'C' | 'T';
  rotation: boolean;
  teams: boolean;
  maxPlayers: number;
  bots: number;
  hpMul: number;
  dmgMul: number;
  xpMul: number;
  startLevel: number;
  maxLevel: number;
  shapes: boolean;
  monsters: boolean;
  boss: boolean;
  protection: boolean;
}

export const PRIVATE_DEFAULTS: PrivateOptions = {
  map: 'A',
  rotation: false,
  teams: false,
  maxPlayers: 16,
  bots: 0,
  hpMul: 1,
  dmgMul: 1,
  xpMul: 1,
  startLevel: 1,
  maxLevel: 60,
  shapes: true,
  monsters: true,
  boss: false,
  protection: true,
};

/** Options bornées pour ne pas casser le serveur. */
export function clampPrivate(o: Partial<Record<keyof PrivateOptions, unknown>>): PrivateOptions {
  const num = (v: unknown, lo: number, hi: number, d: number) => (typeof v === 'number' && Number.isFinite(v) ? Math.min(hi, Math.max(lo, v)) : d);
  const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
  const map = o.map === 'A' || o.map === 'B' || o.map === 'C' || o.map === 'T' ? o.map : PRIVATE_DEFAULTS.map;
  const startLevel = Math.round(num(o.startLevel, 1, 60, 1));
  return {
    map,
    rotation: bool(o.rotation, false),
    teams: bool(o.teams, false),
    maxPlayers: Math.round(num(o.maxPlayers, 1, 40, 16)),
    bots: Math.round(num(o.bots, 0, 20, 0)),
    hpMul: num(o.hpMul, 0.5, 3, 1),
    dmgMul: num(o.dmgMul, 0.5, 3, 1),
    xpMul: num(o.xpMul, 0.5, 5, 1),
    startLevel,
    maxLevel: Math.round(num(o.maxLevel, startLevel, 60, 60)),
    shapes: bool(o.shapes, true),
    monsters: bool(o.monsters, true),
    boss: bool(o.boss, false),
    protection: bool(o.protection, true),
  };
}

export const PRIVATE_LOBBY_HOURS = 4;

/** Messages rapides : un index transmis, traduit dans la langue de chaque destinataire. */
export const QUICK_MESSAGES = ['gg', 'warning', 'joinMe', 'thanks', 'help', 'retreat'] as const;

export interface ModeInfo {
  kind: ModeKind;
  phase: 'waiting' | 'running' | 'overtime' | 'prep' | 'ended';
  timeLeft: number;
  scores?: number[];
  target?: number;
  points?: { x: number; y: number; owner: number; progress: number; team: number }[];
  wave?: number;
  waves?: number;
  difficulty?: DifficultyId;
  winner?: number | null;
  noRewards?: boolean;
  code?: string;
  host?: boolean;
  bots?: number;
  waitS?: number;
}
